import { useCallback, useRef, useState } from "react";

const QUEUE_KEY = "moldex-admin-queue";

/** Set of asset IDs queued for disposition, persisted in sessionStorage. */
export function useAdminQueue() {
  const [queued, setQueued] = useState<Set<string>>(() => loadInitial());
  // Anchor for shift-click range queueing — the last individually-toggled card.
  const anchorRef = useRef<string | null>(null);

  const persist = useCallback((next: Set<string>) => {
    try {
      sessionStorage.setItem(QUEUE_KEY, JSON.stringify([...next]));
    } catch {
      // sessionStorage full / disabled — degrade silently
    }
  }, []);

  const toggle = useCallback(
    (assetId: string) => {
      setQueued((prev) => {
        const next = new Set(prev);
        if (next.has(assetId)) next.delete(assetId);
        else next.add(assetId);
        persist(next);
        return next;
      });
      anchorRef.current = assetId;
    },
    [persist],
  );

  /**
   * Marquee / bulk add. ALWAYS additive — this is a removal queue, so a drag
   * must never silently drop already-queued items the way a replacing
   * download-selection marquee can.
   */
  const addMany = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      setQueued((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        persist(next);
        return next;
      });
      const last = ids[ids.length - 1];
      if (last != null) anchorRef.current = last;
    },
    [persist],
  );

  /** Shift-click: queue everything between the anchor and `id` in display order. */
  const queueRange = useCallback(
    (orderedIds: string[], id: string) => {
      const anchor = anchorRef.current;
      const i = anchor == null ? -1 : orderedIds.indexOf(anchor);
      const j = orderedIds.indexOf(id);
      if (i === -1 || j === -1) {
        toggle(id);
        return;
      }
      const [lo, hi] = i <= j ? [i, j] : [j, i];
      addMany(orderedIds.slice(lo, hi + 1));
      anchorRef.current = id;
    },
    [addMany, toggle],
  );

  const remove = useCallback(
    (assetId: string) => {
      setQueued((prev) => {
        if (!prev.has(assetId)) return prev;
        const next = new Set(prev);
        next.delete(assetId);
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const clear = useCallback(() => {
    setQueued(new Set());
    persist(new Set());
    anchorRef.current = null;
  }, [persist]);

  return { queued, toggle, remove, clear, addMany, queueRange };
}

function loadInitial(): Set<string> {
  try {
    if (typeof window === "undefined") return new Set();
    const raw = sessionStorage.getItem(QUEUE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}
